import React, { useState, useEffect, useCallback } from "react";
import { Spin } from "antd";
import * as recruitmentApi from "../../../../../core/data/redux/services/recruitmentApi";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";
import { notify } from "../../../../../services/toast.config";

interface ApplicationsTabProps {
  onViewTimeline: (app: any) => void;
  primaryColor?: string;
}


const STATUS_MAP: Record<string, { label: string; color: string; bg: string; border: string; icon: string }> = {
  applied: { label: "Applied", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", icon: "ti-send" },
  screening: { label: "Screening", color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd", icon: "ti-eye" },
  shortlisted: { label: "Shortlisted", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", icon: "ti-star" },
  interview: { label: "Interview", color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: "ti-calendar-event" },
  offer: { label: "Offer", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", icon: "ti-gift" },
  hired: { label: "Hired", color: "#047857", bg: "#d1fae5", border: "#6ee7b7", icon: "ti-circle-check" },
  rejected: { label: "Rejected", color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: "ti-circle-x" },
  withdrawn: { label: "Withdrawn", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", icon: "ti-circle-minus" },
};
const getStatus = (s: string) =>
  STATUS_MAP[s?.toLowerCase()] ?? { label: s || "Unknown", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", icon: "ti-circle" };

const FILTER_ORDER = ["all", "applied", "screening", "shortlisted", "interview", "offer", "hired", "rejected", "withdrawn"];

const atsColor = (score: number) =>
  score >= 80 ? { color: "#047857", bg: "#d1fae5", border: "#6ee7b7" }
    : score >= 60 ? { color: "#b45309", bg: "#fef3c7", border: "#fde68a" }
      : { color: "#b91c1c", bg: "#fee2e2", border: "#fca5a5" };


const StatCard: React.FC<{ label: string; value: number; icon: string; color: string; bg: string; border: string }> = ({
  label, value, icon, color, bg, border,
}) => (
  <div style={{
    background: bg,
    border: `1.5px solid ${border}`,
    borderRadius: 16,
    padding: "18px 20px",
    display: "flex",
    alignItems: "center",
    gap: 16,
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12,
      background: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      color, fontSize: 20, flexShrink: 0,
      boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
    }}>
      <i className={`ti ${icon}`} />
    </div>
    <div>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1, letterSpacing: "-0.5px" }}>{value}</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 5, fontWeight: 500 }}>{label}</div>
    </div>
  </div>
);


const FilterPill: React.FC<{ f: string; isActive: boolean; count: number; onClick: () => void; primaryColor: string }> = ({
  f, isActive, count, onClick, primaryColor,
}) => {
  const sc = f !== "all" ? getStatus(f) : null;
  const activeColor = sc?.color ?? primaryColor;
  const activeBg = sc?.bg ?? `${primaryColor}12`;
  const activeBorder = sc?.border ?? `${primaryColor}40`;
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "5px 12px",
        borderRadius: 20,
        fontSize: 12, fontWeight: isActive ? 700 : 500,
        cursor: "pointer",
        border: `1.5px solid ${isActive ? activeBorder : "#e5e7eb"}`,
        background: isActive ? activeBg : "#fff",
        color: isActive ? activeColor : "#6b7280",
        transition: "all 0.15s",
        whiteSpace: "nowrap",
        boxShadow: isActive ? `0 2px 8px ${activeColor}18` : "none",
      }}
    >
      {sc && <i className={`ti ${sc.icon}`} style={{ fontSize: 11 }} />}
      {f === "all" ? "All" : sc?.label}
      <span style={{
        fontSize: 11, fontWeight: 700,
        background: isActive ? "rgba(0,0,0,0.09)" : "#f3f4f6",
        color: isActive ? activeColor : "#9ca3af",
        borderRadius: 20, padding: "1px 6px",
      }}>
        {count}
      </span>
    </button>
  );
};


const AppCard: React.FC<{ app: any; primaryColor: string; onViewTimeline: (app: any) => void }> = ({
  app, primaryColor, onViewTimeline,
}) => {
  const [hovered, setHovered] = useState(false);
  const sc = getStatus(app.status);
  const isTerminal = ["rejected", "withdrawn"].includes(app.status?.toLowerCase());
  const ats = app.ats_score != null ? atsColor(app.ats_score) : null;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        border: `1.5px solid ${hovered ? sc.border : "#eaecf0"}`,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: hovered ? "0 8px 28px rgba(0,0,0,0.10)" : "0 1px 4px rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "all 0.2s ease",
      }}
    >
      
      <div style={{
        height: 4,
        background: `linear-gradient(90deg, ${sc.color}, ${sc.color}88)`,
        opacity: isTerminal ? 0.4 : 1,
      }} />

      <div style={{ padding: "20px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>

          
          <div style={{ flex: 1, minWidth: 0 }}>

            
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
              <h5 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: 0, lineHeight: 1.3 }}>
                {app?.job_title || "Position Title"}
              </h5>
              
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "3px 10px",
                borderRadius: 20,
                fontSize: 11, fontWeight: 700,
                background: sc.bg,
                color: sc.color,
                border: `1.5px solid ${sc.border}`,
                whiteSpace: "nowrap",
              }}>
                <i className={`ti ${sc.icon}`} style={{ fontSize: 10 }} />
                {sc.label}
              </span>
            </div>


            
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 12 }}>
              {app.employment_type && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 6, background: "#f3f4f6",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className="ti ti-clock" style={{ fontSize: 11, color: "#9ca3af" }} />
                  </span>
                  {app.employment_type}
                </span>
              )}
              {app.work_location && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 6, background: "#f3f4f6",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className="ti ti-map-pin" style={{ fontSize: 11, color: "#9ca3af" }} />
                  </span>
                  {app.work_location}
                </span>
              )}
              {app.work_mode && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 6, background: "#f3f4f6",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className="ti ti-home" style={{ fontSize: 11, color: "#9ca3af" }} />
                  </span>
                  {app.work_mode}
                </span>
              )}
            </div>

            
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              {app.application_code && (
                <span style={{
                  fontSize: 11, color: "#6b7280",
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "4px 9px",
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  borderRadius: 7,
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  letterSpacing: "0.02em",
                }}>
                  <i className="ti ti-hash" style={{ fontSize: 10, color: "#9ca3af" }} />
                  {app.application_code}
                </span>
              )}
              {ats && (
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  padding: "4px 10px", borderRadius: 7,
                  background: ats.bg, color: ats.color,
                  display: "inline-flex", alignItems: "center", gap: 5,
                  border: `1.5px solid ${ats.border}`,
                }}>
                  <i className="ti ti-chart-bar" style={{ fontSize: 10 }} />
                  ATS Match {app.ats_score}%
                </span>
              )}
            </div>

            

            {app.latest_stage?.candidate_message && (
              <div style={{
                marginTop: 12,
                padding: "10px 14px",
                background: isTerminal ? "#fef2f2" : "#f8fafc",
                borderRadius: 10,
                fontSize: 12.5,
                color: isTerminal ? "#991b1b" : "#374151",
                borderLeft: `3px solid ${sc.color}`,
                lineHeight: 1.55,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
              }}>
                <span>
                  <i className={`ti ${sc.icon}`} style={{ marginRight: 7, color: sc.color, fontSize: 12 }} />
                  {app.latest_stage.candidate_message}
                </span>
                {app.latest_stage?.changed_at && (
                  <span style={{
                    fontSize: 11,
                    color: isTerminal ? "#b91c1c" : "#9ca3af",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    opacity: 0.8,
                    flexShrink: 0,
                  }}>
                    <i className="ti ti-clock" style={{ fontSize: 11 }} />
                    {humanReadableDate(app.latest_stage.changed_at)}
                  </span>
                )}
              </div>
            )}
          </div>

          
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "flex-end", justifyContent: "space-between",
            gap: 16, flexShrink: 0,
          }}>
            
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 9999,
              padding: "6px 10px",
              whiteSpace: "nowrap",
              color: "#475569",
            }}>
              <i className="ti ti-calendar" style={{ fontSize: 14, color: "#64748b" }} />

              <span style={{ opacity: 0.7 }}>
                Applied
              </span>

              <span style={{ fontWeight: 500, color: "#0f172a" }}>
                {humanReadableDate(app.applied_at)}
              </span>
            </div>

            
            {!isTerminal ? (
              <button
                onClick={() => onViewTimeline(app)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7,
                  padding: "10px 18px",
                  background: primaryColor,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 13, fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: `0 3px 12px ${primaryColor}40`,
                  transition: "opacity 0.15s, transform 0.15s",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = "0.88"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
              >
                <i className="ti ti-radar" style={{ fontSize: 13 }} />
                Track Status
              </button>
            ) : (
              <button
                onClick={() => onViewTimeline(app)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "9px 15px",
                  background: "#f9fafb",
                  color: "#6b7280",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: 10,
                  fontSize: 13, fontWeight: 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="ti ti-eye" style={{ fontSize: 13 }} />
                View Details
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const EmptyState: React.FC<{ filter: string; primaryColor: string }> = ({ filter, primaryColor }) => (
  <div style={{
    background: "#fff",
    border: "1.5px dashed #e5e7eb",
    borderRadius: 16,
    padding: "64px 24px",
    textAlign: "center",
  }}>
    <div style={{
      width: 72, height: 72, borderRadius: "50%",
      background: `${primaryColor}0d`,
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto 20px",
      fontSize: 30, color: primaryColor,
    }}>
      <i className="ti ti-folder-off" />
    </div>
    <h5 style={{ fontSize: 17, fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>
      {filter === "all" ? "No applications yet" : `No ${getStatus(filter).label.toLowerCase()} applications`}
    </h5>
    <p style={{ fontSize: 13.5, color: "#9ca3af", margin: 0, lineHeight: 1.6 }}>
      {filter === "all"
        ? "Start applying to jobs and track your progress here."
        : `You don't have any ${getStatus(filter).label.toLowerCase()} applications right now.`}
    </p>
  </div>
);


const ApplicationsTab: React.FC<ApplicationsTabProps> = ({
  onViewTimeline,
  primaryColor = "#4f46e5",
}) => {
  const [apps, setApps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const data = await recruitmentApi.getMyApplications();
      setApps(data?.applications || (Array.isArray(data) ? data : []));
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const filtered = filter === "all" ? apps : apps.filter(a => a.status?.toLowerCase() === filter);
  const activeCount = apps.filter(a => !["rejected", "withdrawn"].includes(a.status?.toLowerCase())).length;
  const interviewCount = apps.filter(a => a.status?.toLowerCase().includes("interview")).length;
  const offerCount = apps.filter(a => ["offer", "hired"].includes(a.status?.toLowerCase())).length;

  if (loading) return (
    <div style={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spin size="large" />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatCard label="Total Applied" value={apps.length} icon="ti-briefcase" color={primaryColor} bg={`${primaryColor}0d`} border={`${primaryColor}30`} />
        <StatCard label="Active" value={activeCount} icon="ti-refresh" color="#059669" bg="#ecfdf5" border="#a7f3d0" />
        <StatCard label="Interviews" value={interviewCount} icon="ti-calendar-event" color="#d97706" bg="#fffbeb" border="#fde68a" />
        <StatCard label="Offers" value={offerCount} icon="ti-gift" color="#7c3aed" bg="#f5f3ff" border="#ddd6fe" />
      </div>

      
      {apps.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: "#9ca3af",
            letterSpacing: "0.08em", textTransform: "uppercase", marginRight: 2,
          }}>
            Filter by
          </span>
          {FILTER_ORDER
            .filter(f => f === "all" || apps.some(a => a.status?.toLowerCase() === f))
            .map(f => {
              const count = f === "all" ? apps.length : apps.filter(a => a.status?.toLowerCase() === f).length;
              return (
                <FilterPill
                  key={f} f={f} isActive={filter === f}
                  count={count} onClick={() => setFilter(f)} primaryColor={primaryColor}
                />
              );
            })}
        </div>
      )}

      
      {apps.length > 0 && (
        <div style={{ fontSize: 12.5, color: "#9ca3af", marginTop: -8, display: "flex", alignItems: "center", gap: 8 }}>
          Showing <strong style={{ color: "#374151", fontWeight: 700 }}>{filtered.length}</strong> application{filtered.length !== 1 ? "s" : ""}
          {filter !== "all" && (
            <>
              <span style={{ color: "#e5e7eb" }}>·</span>
              <button
                onClick={() => setFilter("all")}
                style={{ background: "none", border: "none", color: primaryColor, cursor: "pointer", fontSize: 12.5, fontWeight: 600, padding: 0 }}
              >
                Clear filter
              </button>
            </>
          )}
        </div>
      )}

      
      {filtered.length === 0 && <EmptyState filter={filter} primaryColor={primaryColor} />}

      
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map(app => (
          <AppCard
            key={app.application_id || app.id}
            app={app}
            primaryColor={primaryColor}
            onViewTimeline={onViewTimeline}
          />
        ))}
      </div>
    </div>
  );
};

export default ApplicationsTab;